/**
 * 잡 진행 폴링을 **jobId 하나당 한 벌**로 묶는다.
 *
 * 🩸 왜 생겼나 — 종전엔 폴링 구현이 둘이었다(`waitForJob` · `useJobProgress`). 그리고 수업의
 *    주 경로인 배치 생성에서 **둘이 같은 jobId 를 동시에** 쳤다: `startImage` 가
 *    `await query.refresh()` 를 하고 «나서» jobId 를 돌려주므로, 루프가 `waitForJob` 을 걸
 *    시점엔 이미 컷 카드의 `useJobProgress` 가 폴링 중이었다. 같은 잡에 같은 답을 두 번 묻는
 *    것이라 얻는 것은 없고 서버 부담만 두 배였다 — 2026-09-04 R11 실측에서 이 사실이
 *    분모를 12건/분 → 23.28건/분으로 두 배 키웠고, 수용 인원을 절반으로 깎고 있었다.
 *
 * 🔑 그래서 «폴링을 누가 요청했는가» 와 «실제로 몇 번 묻는가» 를 분리한다. 구독자는 몇이든
 *    좋고, 네트워크 요청은 jobId 마다 한 줄기다. 늦게 붙은 구독자는 지금까지의 답을 즉시 받는다.
 *
 * 🚨 이 파일은 `api.ts` 를 **부르지 않는다**(순환 임포트 금지). 무엇을 가져올지·무엇이 끝인지는
 *    호출부가 넘긴다. 그 덕에 시험이 진짜 폴링 루프를 fake fetcher 로 돌려 «요청 수» 를 셀 수 있다.
 */

export interface JobPollUpdate<P> {
  progress: P | null;
  stopped: boolean;
  /** 마지막으로 잡힌 예외. 성공하면 null 로 지워진다. 형(型)은 호출부가 정한다. */
  error: unknown;
}

export interface JobPollDriver<P> {
  fetchProgress: (jobId: string) => Promise<P>;
  isTerminal: (progress: P) => boolean;
}

interface Entry<P> {
  listeners: Set<(update: JobPollUpdate<P>) => void>;
  last: JobPollUpdate<P>;
  timer: ReturnType<typeof setTimeout> | null;
}

const entries = new Map<string, Entry<unknown>>();

/** 정상 간격 — 500ms 에서 시작해 5초로 수렴한다(0.8~1.2 지터). 제품 상수라 바꾸면 R11 을 다시 잰다. */
function nextDelayMs(attempt: number): number {
  const base = Math.min(500 * 1.45 ** attempt, 5_000);
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

/** 실패 간격 — 750ms 에서 8초까지 배로 늘린다. */
function retryDelayMs(consecutiveFailures: number): number {
  const base = Math.min(750 * 2 ** (consecutiveFailures - 1), 8_000);
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

/** 🚨 다섯 번 연속 실패하면 «멈췄다» 고 알린다 — 영영 기다리면 «다음 컷» 이 시작되지 않아 화면이 통째로 선다. */
const MAX_CONSECUTIVE_FAILURES = 5;
/**
 * 멈췄다고 알린 뒤에도 구독자가 남아 있으면 이 간격으로 조용히 다시 묻는다.
 * 🩸 종전엔 거기서 끝이라, 연결이 돌아와도 편집실 AI 영상 표시·「완성하기」 잠금이 화면을 다시 열 때까지 남았다
 *    (Codex 10/23 새 발견 2번). 실패 중에만 도는 줄기라 평소 요청 수(R11 분모)는 그대로다.
 */
export const JOB_POLL_RECOVERY_MS = 15_000;

function emit<P>(entry: Entry<P>, patch: Partial<JobPollUpdate<P>>): void {
  entry.last = { ...entry.last, ...patch };
  for (const listener of [...entry.listeners]) listener(entry.last);
}

function startLoop<P>(jobId: string, entry: Entry<P>, driver: JobPollDriver<P>): void {
  let attempt = 0;
  let consecutiveFailures = 0;

  const poll = async (): Promise<void> => {
    let next: P;
    try {
      next = await driver.fetchProgress(jobId);
    } catch (caught) {
      // 🚨 구독자가 도중에 전부 떠났으면 더 묻지 않는다(끊긴 뒤 한 번 더 도는 것을 막는다).
      if (!entries.has(jobId)) return;
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        emit(entry, { error: caught, stopped: true });
        // 🔑 기다리던 쪽(`waitForJob`)은 여기서 풀려 다음으로 간다. 화면처럼 계속 보는 구독자가 남았으면 천천히 다시 묻는다.
        entry.timer = setTimeout(() => void poll(), JOB_POLL_RECOVERY_MS);
        return;
      }
      emit(entry, { error: caught });
      entry.timer = setTimeout(() => void poll(), retryDelayMs(consecutiveFailures));
      return;
    }
    if (!entries.has(jobId)) return;
    consecutiveFailures = 0;
    if (driver.isTerminal(next)) {
      emit(entry, { progress: next, error: null, stopped: true });
      return;
    }
    emit(entry, { progress: next, error: null });
    entry.timer = setTimeout(() => void poll(), nextDelayMs(attempt));
    attempt += 1;
  };

  void poll();
}

/**
 * jobId 를 구독한다. 반환값을 부르면 구독이 끊기고, **마지막 구독자가 떠나면 폴링도 멈춘다.**
 *
 * 🔑 이미 도는 줄기가 있으면 새 요청을 만들지 않고 지금까지의 답을 그 자리에서 건넨다.
 */
export function watchJob<P>(
  jobId: string,
  driver: JobPollDriver<P>,
  listener: (update: JobPollUpdate<P>) => void,
): () => void {
  const existing = entries.get(jobId) as Entry<P> | undefined;
  const entry: Entry<P> = existing ?? {
    listeners: new Set(),
    last: { progress: null, stopped: false, error: null },
    timer: null,
  };
  entry.listeners.add(listener);
  if (!existing) {
    entries.set(jobId, entry as Entry<unknown>);
    startLoop(jobId, entry, driver);
  } else {
    listener(entry.last);
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.timer = null;
    entries.delete(jobId);
  };
}

/** 지금 폴링이 도는 jobId 수 — 시험 전용(제품 코드에서 부르지 말 것). */
export function activeJobPollCountForTest(): number {
  return entries.size;
}

/** 시험 사이 격리용. 제품 코드에서 부르지 말 것. */
export function resetJobPollsForTest(): void {
  for (const entry of entries.values()) {
    if (entry.timer !== null) clearTimeout(entry.timer);
  }
  entries.clear();
}
